import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BadgePercent, Play, Volume2, VolumeX, X } from "lucide-react";
import { api } from "@/lib/api";

const SEEN_KEY = "paqvero_offers_seen";
const SEEN_AT_KEY = "paqvero_offers_seen_at";

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function shouldShow(cfg) {
  const f = cfg?.frequency || "daily";
  if (f === "every_visit") return true;
  if (f === "interval") {
    const at = parseInt(localStorage.getItem(SEEN_AT_KEY) || "0", 10);
    return Date.now() - at >= (cfg?.interval_hours || 3) * 3600 * 1000;
  }
  return localStorage.getItem(SEEN_KEY) !== todayKey();
}

/** Fallback panel when media fails — branded gradient, text + CTA intact. */
function MediaFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(135deg,#242521_0%,#3a3b34_55%,#C88732_130%)]">
      <BadgePercent className="h-14 w-14 text-[#E4B66D]/70" />
    </div>
  );
}

function StageMedia({ offer, muted, onToggleMute, reduced }) {
  const [failed, setFailed] = useState(false);
  const videoRef = useRef(null);
  useEffect(() => { setFailed(false); }, [offer.id]);
  useEffect(() => {
    const v = videoRef.current;
    if (v) { v.muted = muted; if (!muted) v.play().catch(() => {}); }
  }, [muted, offer.id]);

  if (!offer.media_url || failed) return <MediaFallback />;
  if (offer.media_type === "video") {
    return (
      <div className="relative h-full w-full">
        <video
          ref={videoRef}
          key={offer.id}
          data-testid="offers-stage-video"
          src={offer.media_url}
          className="h-full w-full object-cover"
          autoPlay={!reduced}
          muted={muted}
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
        />
        <button
          type="button"
          data-testid="offers-video-sound-toggle"
          onClick={onToggleMute}
          aria-label={muted ? "Unmute video" : "Mute video"}
          className="absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition-colors hover:bg-black/75"
        >
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      </div>
    );
  }
  return (
    <img
      key={offer.id}
      data-testid="offers-stage-image"
      src={offer.media_url}
      alt={offer.title}
      className="h-full w-full object-cover"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

export default function OffersSpotlight() {
  const [offers, setOffers] = useState(null);
  const [open, setOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [muted, setMuted] = useState(true);
  const navigate = useNavigate();
  const reduced = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    Promise.all([api.get("/offers/live"), api.get("/offers/config")])
      .then(([liveRes, cfgRes]) => {
        const data = liveRes.data;
        if (!shouldShow(cfgRes.data)) return;
        if (Array.isArray(data) && data.length > 0) {
          setOffers(data);
          setActiveId(data[0].id);
          const t = setTimeout(() => setOpen(true), 1200); // a brief beat after load
          return () => clearTimeout(t);
        }
      })
      .catch(() => {});
  }, []);

  const dismiss = useCallback(() => {
    localStorage.setItem(SEEN_KEY, todayKey());
    localStorage.setItem(SEEN_AT_KEY, String(Date.now()));
    setOpen(false);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [open, dismiss]);

  const active = offers?.find((o) => o.id === activeId) || offers?.[0];
  const goCta = (o) => {
    dismiss();
    if (/^https?:\/\//.test(o.cta_link)) window.open(o.cta_link, "_blank", "noopener");
    else navigate(o.cta_link || "/shop");
  };

  if (!offers || offers.length === 0) return null;

  return createPortal(
    <AnimatePresence>
      {open && active && (
        <motion.div
          data-testid="offers-spotlight"
          role="dialog"
          aria-modal="true"
          aria-label="Latest offers"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduced ? 0 : 0.25 }}
          className="fixed inset-0 z-[80] flex items-end justify-center bg-black/55 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={dismiss}
        >
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduced ? { opacity: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="flex h-[100dvh] w-full flex-col overflow-hidden bg-surface sm:h-auto sm:max-h-[86vh] sm:max-w-3xl sm:flex-row sm:rounded-3xl sm:border sm:border-line sm:shadow-lift"
          >
            {/* rail — desktop left column / mobile bottom swipe strip */}
            <div
              data-testid="offers-rail"
              className="order-2 flex shrink-0 gap-2 overflow-x-auto border-t border-line p-3 sm:order-1 sm:w-44 sm:flex-col sm:overflow-y-auto sm:overflow-x-hidden sm:border-r sm:border-t-0"
              data-lenis-prevent
            >
              <p className="hidden px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-3 sm:block">Today's offers</p>
              {offers.map((o) => (
                <button
                  key={o.id}
                  data-testid={`offers-rail-item-${o.id}`}
                  onClick={() => { setActiveId(o.id); setMuted(true); }}
                  className={`group flex w-36 shrink-0 items-center gap-2.5 rounded-xl border p-2 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card sm:w-full motion-reduce:hover:translate-y-0 ${
                    o.id === active.id ? "border-amber-500 bg-amber-500/10" : "border-line bg-paper"
                  }`}
                >
                  <span className="relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-[#242521]">
                    {o.media_url && o.media_type === "image" ? (
                      <img src={o.media_url} alt="" className="h-full w-full object-cover" loading="lazy" />
                    ) : (
                      <BadgePercent className="h-5 w-5 text-[#E4B66D]" />
                    )}
                    {o.media_type === "video" && (
                      <span className="absolute inset-0 flex items-center justify-center bg-black/35">
                        <Play className="h-4 w-4 fill-white text-white" />
                      </span>
                    )}
                  </span>
                  <span className="min-w-0">
                    {o.badge && <span className="block truncate text-[10px] font-extrabold uppercase tracking-wider text-amber-600">{o.badge}</span>}
                    <span className="block truncate text-xs font-bold text-1">{o.title}</span>
                  </span>
                </button>
              ))}
            </div>

            {/* stage */}
            <div className="relative order-1 flex min-h-0 flex-1 flex-col sm:order-2">
              <button
                type="button"
                data-testid="offers-close"
                onClick={dismiss}
                aria-label="Close offers"
                className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur transition-colors hover:bg-black/70"
              >
                <X className="h-4 w-4" />
              </button>

              <div className="relative h-64 shrink-0 overflow-hidden bg-[#242521] sm:h-72 sm:rounded-tl-none">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={active.id}
                    initial={reduced ? false : { opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={reduced ? undefined : { opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    className="h-full w-full"
                  >
                    <StageMedia offer={active} muted={muted} onToggleMute={() => setMuted((m) => !m)} reduced={reduced} />
                  </motion.div>
                </AnimatePresence>
              </div>

              <div className="flex flex-1 flex-col gap-2 p-5 sm:p-6">
                {active.badge && (
                  <span data-testid="offers-stage-badge" className="inline-flex w-fit rounded-full bg-amber-500/12 px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.16em] text-amber-600">
                    {active.badge}
                  </span>
                )}
                <h2 data-testid="offers-stage-title" className="font-display text-2xl font-semibold leading-tight text-1 sm:text-3xl">
                  {active.title}
                </h2>
                {active.description && <p className="text-sm leading-relaxed text-3">{active.description}</p>}
                <div className="mt-auto pt-3">
                  <button
                    type="button"
                    data-testid="offers-stage-cta"
                    onClick={() => goCta(active)}
                    className="w-full rounded-full bg-amber-500 px-6 py-3 text-sm font-extrabold uppercase tracking-[0.12em] text-white transition-colors duration-300 hover:bg-amber-600 sm:w-auto"
                  >
                    {active.cta_label || "Shop now"} →
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
